import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Notification, toast } from 'components/ui'
import { APP_PREFIX_PATH } from 'constants/route.constant'
import useLiveVisitors from 'utils/hooks/useLiveVisitors'
import { useStickyActionsHeight } from 'utils/hooks/useStickyActions'
import { countryFlag, locationLabel } from 'utils/countryFlag'

/** Reactive media query — pill behaviour differs below the tablet breakpoint. */
const useIsMobile = () => {
    const [mobile, setMobile] = useState(
        typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
    )
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)')
        const fn = (e) => setMobile(e.matches)
        mq.addEventListener('change', fn)
        return () => mq.removeEventListener('change', fn)
    }, [])
    return mobile
}

/**
 * Global live-visitor widget, mounted once for the whole authenticated app.
 * - Polls the live-visitors endpoint (~12s).
 * - Fires a toast when a new visitor lands on the website.
 * - Renders an always-visible "N online" pill (bottom-right) that links to the
 *   full Live Visitors feed.
 */
const LiveVisitors = () => {
    const navigate = useNavigate()
    const location = useLocation()

    const onNewVisitor = useCallback((v) => {
        const page = v.currentPath || v.entryPath || '/'
        toast.push(
            <Notification type="info" title="New visitor" duration={5000}>
                <span className="mr-1">{countryFlag(v.country)}</span>
                {locationLabel(v)} — viewing <span className="font-semibold">{page}</span>
            </Notification>,
            { placement: 'top-end' }
        )
    }, [])

    // A browsing visitor just turned high-intent (reached checkout, or is on a
    // high-value item) — surface a prominent, longer-lived alert so it can be
    // acted on.
    const onHighIntent = useCallback((v) => {
        const where = v.intent === 'checkout'
            ? 'is at checkout'
            : `is viewing ${v.productTitle || 'a high-value item'}${v.productValue ? ` (AED ${Number(v.productValue).toLocaleString('en-AE')})` : ''}`
        toast.push(
            <Notification type="warning" title="🔥 High-intent visitor" duration={10000}>
                <span className="mr-1">{countryFlag(v.country)}</span>
                {locationLabel(v)} <span className="font-semibold">{where}</span>
            </Notification>,
            { placement: 'top-end' }
        )
    }, [])

    const { onlineCount } = useLiveVisitors({ intervalMs: 12000, onNewVisitor, onHighIntent })
    const stickyHeight = useStickyActionsHeight()
    const isMobile = useIsMobile()
    // Dismissible per session — the floating pill covers footer links and
    // action buttons (worst on phones). Hiding it never stops polling or
    // toasts, and the full feed stays under Analytics → Live Visitors.
    const [hidden, setHidden] = useState(() => sessionStorage.getItem('lvPillHidden') === '1')

    // Keep polling + toasts running everywhere, but hide the floating pill on the
    // Live Visitors page itself — it's redundant there and overlaps the feed on mobile.
    if (location.pathname.includes('/analytics/live-visitors')) return null

    // Sticky page actions always win over utility widgets: on phones, pages
    // with a sticky Save/Publish bar hide the pill entirely (polling and
    // toasts continue). On tablet/desktop it repositions above the measured
    // bar instead — no hardcoded bottom offsets anywhere.
    if (isMobile && stickyHeight > 0) return null
    if (hidden) return null

    const dismiss = (e) => {
        e.stopPropagation()
        sessionStorage.setItem('lvPillHidden', '1')
        setHidden(true)
    }

    return (
        <div
            className="fixed right-5 z-10"
            style={{
                // Measured bar height + breathing room + device safe area.
                bottom: `calc(1rem + env(safe-area-inset-bottom, 0px) + var(--mobile-sticky-actions-height, 0px))`,
            }}
        >
            <button
                type="button"
                onClick={() => navigate(`${APP_PREFIX_PATH}/analytics/live-visitors`)}
                // z-10: above page content, below sticky actions (z-20) and
                // drawers/dialogs (z-30) — see utils/hooks/useStickyActions.js.
                // Mobile gets the compact form (dot + count) — the full pill
                // covered footer links and action buttons on small screens.
                className={`flex items-center gap-2 rounded-full bg-gray-900 text-white shadow-lg transition-colors hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 ${
                    isMobile ? 'min-h-[36px] px-3 py-1.5' : 'min-h-[44px] px-4 py-2'
                }`}
                title="Live visitors — click for the full feed"
            >
                <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <span className={`font-semibold ${isMobile ? 'text-[12.5px]' : 'text-sm'}`}>
                    {isMobile ? onlineCount : `${onlineCount} online`}
                </span>
            </button>
            <button
                type="button"
                aria-label="Hide the live visitors pill until next login"
                onClick={dismiss}
                className="absolute -left-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-gray-600 text-[11px] leading-none text-white shadow hover:bg-gray-500"
            >
                ×
            </button>
        </div>
    )
}

export default LiveVisitors
